/**
 * PaywallPage — 支付管理 / Paywall（ADM-PAY-S001~S004 · ADM-PAY-001/002/005）
 *
 * 移植自原型 design/Novel-admin/src/pages/paywall.jsx + 全面接真（goal PR9）。
 * 原生 App 使用支付页面、产品与 Offer；WEB 只展示自家商品目录，不挂载原生查询或配置面。
 *
 * 接真要点：
 *  - 「支付页面」tab：原死常量 mock 死常量 → usePaymentPages(showApp.id)（真 paymentPagesApi.list +
 *    每页 listPaywalls，N+1）。读修缮稳定 fallback（空集），enabled=false（无真实 App）返稳定空集。
 *  - 写：新建（NewPaywallPageDialog→create）/ 保存配置（upsertPaywall 草稿，单按钮·随设计稿，不动上下架）/
 *    上架·下架（header togglePageMutation→update is_active）/ 删除（DeletePaywallDialog→remove，ADM-PAY-S004）。
 *  - RBAC：create/update 写动作角色集 root/admin/operator（对齐 server route requireRole）；删除收窄 root/admin
 *    （F1：级联软删 C 端 app_payment_methods 高危）；viewer 只读 → 写控件 disabled + title。
 *  - F5：某页 listPaywalls 拉取失败（paywallsError）→ 该页 placement 写 / 保存 / 上架 disabled，
 *    避免在残缺渠道数据上 publish 覆盖真实配置（仅显式成功才放行写）。
 *  - 视觉 4 条：操作日志 button / 删除 button（系统页 disabled+lock，运营页 trash+删除弹窗）/ per-channel
 *    placement 存储 + 「真 Adapty 同步存在」状态卡（plInList 绿「已同步」）/ 保存配置单按钮（草稿存·gate !dirty，随设计稿）+ 右对齐说明块。
 *  - 原生价格由 Adapty 承接；Web 价格由独立 API 回读 Stripe，页面只展示真实核验状态。
 */

import type { SettlementChannel } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createElement, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useBlocker, useSearchParams } from 'react-router-dom';
import { AdminApiError } from '~/api/errors';
import { useAppScope } from '~/app/app-scope-store';
import { isPaymentProviderChannel } from '~/data/adapters/acquiring-prerequisite';
import { createPaymentMethodOptionLookup } from '~/data/adapters/payment-method-option-lookup';
import {
  PAYWALL_CHANNEL_IDS,
  settlementChannelOfPaywallChannelId,
} from '~/data/adapters/paywall-adapter';
import {
  paymentProviderConfigRoute,
  useAcquiringPrerequisiteStates,
} from '~/data/payment-provider-config-queries';
import type { AppRow, PaywallPageItem } from '~/data/queries';
import {
  appQueryKeys,
  useAdaptyCatalog,
  useAppDetail,
  useApps,
  useAuditLogsPage,
  usePaymentMethodOptions,
  usePaymentMethodPolicies,
  usePaymentPages,
} from '~/data/queries';
import { adaptyApi } from '~/hooks/use-adapty';
import { paymentPagesApi } from '~/hooks/use-payments';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { staleSnapshotErrorMessage } from '~/lib/stale-snapshot-error';
import { useAuthStore } from '~/stores/auth-store';
import { AppIcon } from '~/ui/AppIcon';
import { ConfirmDialog, Drawer } from '~/ui/Dialog';
import { Select } from '~/ui/Select';
import { KpiSkeleton, SectionSkeleton, Sk } from '~/ui/Skeleton';
import { Switch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { channelDisplayLabel } from '../apps/payment-method-labels';
import { OfferMirror } from './OfferMirror';
import { PaywallProductsPanel } from './PaywallProductsPanel';
import { WebPaymentProductsPanel } from './WebPaymentProductsPanel';
import { DeletePaywallDialog } from './dialogs/DeletePaywallDialog';
import { NewPaywallPageDialog } from './dialogs/NewPaywallPageDialog';
import { ProductOrderDialog } from './dialogs/ProductOrderDialog';

// 同步按钮的 spinner 样式（照原型一次性注入 <style>，避免重复）
if (typeof document !== 'undefined' && !document.getElementById('pw-spinner-style')) {
  const s = document.createElement('style');
  s.id = 'pw-spinner-style';
  s.textContent = `
    .pw-spinner {
      width: 10px; height: 10px;
      border: 1.5px solid var(--fg-faint);
      border-top-color: var(--accent);
      border-radius: 999px;
      display: inline-block;
      animation: pw-spin 0.7s linear infinite;
      margin-right: 4px;
    }
    @keyframes pw-spin { to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(s);
}

/**
 * 从 placement_id 命名约定派生类型标签（对照原型 paywall.jsx PLACEMENT_KIND/buildAdaptyPlacements）。
 * catalog DTO 无 kind 字段 → admin 侧只读派生（不动 C 端表）；原生 datalist 无法渲染 chip，
 * 以纯文本前缀放进选项 label 传达类型。test/onboarding 先判（如 `recharge_v2_ab_test` 归「测试」非「充值」）。
 */
function placementKindLabel(placementId: string): string {
  const id = placementId.toLowerCase();
  if (id.includes('ab_test') || id.includes('_test')) return '测试';
  if (id.includes('onboarding')) return '引导';
  if (id.includes('recharge')) return '充值';
  if (id.includes('promo')) return '特惠';
  if (id.includes('first')) return '首充';
  return '其它';
}

/** 渠道卡片副标题（结算通道 → 收单形态说明）；按 `SettlementChannel` 建表，闭集新增通道时编译期提示补齐。 */
const PAYWALL_CHANNEL_DESC: Record<SettlementChannel, string> = {
  IAP: 'App Store / Google Play',
  STRIPE: '卡支付 / SEPA',
  PAYERMAX: 'PayerMax 收银台 / 卡支付',
  AIRWALLEX: 'Airwallex 收银台',
};

function paywallChannelDescOf(channelId: string): string {
  const channel = settlementChannelOfPaywallChannelId(channelId);
  return channel === null ? '' : PAYWALL_CHANNEL_DESC[channel];
}

/** 各渠道 placement 草稿：闭集内每个渠道一个键，未配置回显空串（不用命名约定伪造初值）。 */
function placementDraftOf(saved: Record<string, string>): Record<string, string> {
  return Object.fromEntries(PAYWALL_CHANNEL_IDS.map((ch) => [ch, saved[ch] ?? '']));
}

/**
 * 渠道行写入键：`payment_method` = 结算通道（PUT paywalls 按 page × 通道 upsert）。不带 `sort_order`：
 * A8 §7.5 请求体闭集三键、带了即 422；渠道展示次序按结算通道闭集（见 paywall-adapter）。
 */
function channelRowKeyOf(channelId: string): { payment_method: SettlementChannel } {
  const channel = settlementChannelOfPaywallChannelId(channelId);
  // 渠道码只来自 PAYWALL_CHANNEL_IDS，闭集外即程序错误：直接抛，不写一个服务端会拒的通道值
  if (channel === null) throw new Error(`未知支付渠道：${channelId}`);
  return { payment_method: channel };
}

/** 写动作角色集（root/admin/operator）；与 server payment-pages route create/update requireRole 精确一致，viewer 只读 */
function useCanWritePaywall(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin' || role === 'operator';
}

/**
 * 删除支付页面角色集（root/admin）；与 server DELETE /payment-pages/:id requireRole 一致（F1：去掉 operator）。
 * Why：删支付页级联软删 app_payment_methods（改 C 端支付可用性）属高危，operator 不应有此权。
 */
function useCanDeletePaywall(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin';
}

/** 当前展示 App（兼容真实 AppRow 与聚合视图；platform 仅真实 AppRow 带）*/
interface ShowApp {
  id: string;
  name: string;
  platform?: string;
}

/**
 * 整页 App 选择门（照原型 PaywallAppGate）——支付管理按 App 独立配置、无「全部 App」聚合视图。
 * 全局选「全部 App」且未选定具体 App 时整页渲染（居中卡片网格），让运营先显式选一个 App 才进支付页；
 * 替代旧「头部内联小 Select 覆盖」的隐式落首个 App。点选回调 onPick(appId) → 切全局 app-scope（setAppId）。
 */
function PaywallAppGate({ apps, onPick }: { apps: AppRow[]; onPick: (id: string) => void }) {
  return (
    <div className="page page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">支付管理</h1>
          <div className="page-sub">
            按 App 管理支付配置 · 原生 App 使用 Adapty，Web 使用独立商品目录
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: '30px 28px' }}>
        <div className="hstack" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icons.IconWallet size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg-strong)' }}>
              支付管理按 App 独立配置，请选择要管理的 App
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.55 }}>
              支付页面、Paywall、渠道启停均按单个 App 维护，无「全部 App」聚合视图 · 选择后进入该
              App 的支付管理（顶部仍可切换）
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
            marginTop: 22,
          }}
        >
          {apps.map((a) => (
            <button
              key={a.id}
              type="button"
              className="focusable"
              onClick={() => onPick(a.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                textAlign: 'left',
                padding: 14,
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--bg-elev)',
                cursor: 'pointer',
                font: 'inherit',
                color: 'var(--fg)',
              }}
            >
              <AppIcon app={a} size="md" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 13,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a.name}
                </div>
                <div
                  className="mono"
                  style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 2 }}
                >
                  {a.id} · {a.platform}
                </div>
              </div>
              <Icons.IconChevRight size={15} style={{ color: 'var(--fg-faint)', flexShrink: 0 }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PaywallPage() {
  // 当前 App 维度：支付管理按 App 独立配置、无「全部 App」聚合视图（照原型 PaywallAppGate）。
  // 全局选「全部 App」（appId==='all'）时 → 渲染整页 App 选择门（见下方 needGate 早返回），
  // 运营显式选一个 App 后 onPick 切**全局** app-scope（setAppId），页面从全局 appId 读维度，
  // 顶栏 AppSwitcher 与本页共用唯一 app-scope-store（消除双真值源）。恰好 1 个真实 App 则
  // effect 自动 setAppId 切全局、不弹门。
  const appId = useAppScope((s) => s.appId);
  const setAppId = useAppScope((s) => s.setAppId);
  const appsQuery = useApps();
  const apps = appsQuery.data;
  const realApps = apps.filter((a) => a.id !== 'all');
  const isAll = appId === 'all';
  const selectedApp = apps.find((app) => app.id === appId);
  // 列表成功但不含当前选中项时，按详情契约确认存在性与平台；不能由缺席猜测平台。
  const selectedAppQuery = useAppDetail(appId, !isAll && !selectedApp && appsQuery.isSuccess);

  const [view, setView] = useState('pages'); // pages | products | offers

  // URL ?tab= 锚点（删除支付页弹窗「去清理」等外部入口跳 /paywall?tab=offers）：
  // 落地/在页时切到指定 tab；一次性消费后即清参（replace，本地 view state 仍是唯一 tab 真值，
  // 避免残留参数干扰后续本地切换与回退历史）。消费 effect 在下方 detailDirty 声明后
  // （切 tab 需过未保存守卫，与页内 tab 点击同一条路径）。
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  // 恰好 1 个真实 App → 自动选中、不弹门（切全局 app-scope，与原型 effect 一致，避免无谓一步选择）
  const soleApp = realApps.length === 1 ? realApps[0] : undefined;
  useEffect(() => {
    if (isAll && soleApp) {
      setAppId(soleApp.id);
    }
  }, [isAll, soleApp, setAppId]);

  // 整页选择门触发条件：全局 'all' + 有 ≥2 个真实 App 可选（1 个由上方 effect 自动选切全局）
  const needGate = isAll && realApps.length > 1;

  // showApp 直接由全局 appId 解析：'all' 时回退占位（仅 effect 切换在途/无真实 App 的瞬态），
  // 否则取选定 App（顶栏 AppSwitcher 与本页同一真值源）。
  const showApp: ShowApp = isAll
    ? (soleApp ?? { id: 'all', name: '全部 App' })
    : (selectedApp ?? selectedAppQuery.data ?? { id: appId, name: appId });

  const isWebApp = showApp.platform?.toUpperCase() === 'WEB';
  const isNativeApp = ['IOS', 'ANDROID'].includes(showApp.platform?.toUpperCase() ?? '');
  const activeView = isWebApp ? 'products' : view;

  // 平台未读取时也不能先请求原生目录；Web 只消费独立商品 API。
  // 「支付页面」tab 真数据：按当前展示 App 拉支付页面 + 渠道配置（替代死常量 mock 死常量）
  const {
    data: pages,
    isLoading: pagesLoading,
    isSuccess: pagesLoaded,
  } = usePaymentPages(isNativeApp ? showApp.id : 'all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 选中页跟随数据：选中失效（删除 / 切 App）时回退首个
  const selected = pages.find((p) => p.id === selectedId) ?? pages[0];
  useEffect(() => {
    if (pages.length && (!selectedId || !pages.some((p) => p.id === selectedId))) {
      setSelectedId(pages[0]?.id ?? null);
    }
  }, [pages, selectedId]);

  const canWrite = useCanWritePaywall();
  const canDelete = useCanDeletePaywall();
  const systemCount = pages.filter((p) => p.system).length;

  // 新建支付页面弹窗本地态（替代 window.openDialog('new-paywall')）
  const [newPaywallOpen, setNewPaywallOpen] = useState(false);
  // 详情未保存改动守卫：PaywallPageDetail 上报 placement 是否有未保存改动；切换支付页面 / 切 tab 前若 dirty
  // 弹「有未保存的更改」确认（继续编辑 / 丢弃更改并离开），避免静默丢弃运营改了一半的 placement 配置。
  const [detailDirty, setDetailDirty] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  // 离开当前详情前的守卫：dirty 时拦截动作弹确认，否则直接执行。
  const guardedLeave = (action: () => void) => {
    if (!detailDirty) {
      action();
      return;
    }
    setPendingLeave(() => action);
  };
  // 消费 ?tab= 锚点：切 tab 同样过未保存守卫（dirty 时弹确认，确认才切；不确认只清参、留在原 tab），
  // 与页内 tab 点击 / 左列表切换同一条守卫路径——外部入口跳转不能静默丢弃未保存的 placement 草稿。
  useEffect(() => {
    if (!tabParam) return;
    if (!isWebApp && !isNativeApp) return;
    if (!isWebApp && (tabParam === 'pages' || tabParam === 'products' || tabParam === 'offers')) {
      if (detailDirty) {
        setPendingLeave(() => () => setView(tabParam));
      } else {
        setView(tabParam);
      }
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      },
      { replace: true },
    );
  }, [tabParam, detailDirty, setSearchParams, isWebApp, isNativeApp]);
  // ?scenario= 锚点（运营弹窗页「去支付页配置」跳 /paywall?tab=pages&scenario=operational_popup）：
  // 支付页列表就绪后选中该 scenario 的页；该 App 还没建这个页 → 提示先新建（A8 §7.2：弹窗落点的写入口
  // 挂在该页上，没有页就没有入口）。一次性消费后清参，与 ?tab= 同款。
  // 只在列表**成功**取回后消费：取数失败时 pages 是空 fallback，按它判会误报「还没有该页」。
  const scenarioParam = searchParams.get('scenario');
  useEffect(() => {
    if (!scenarioParam || !isNativeApp || !pagesLoaded) return;
    const target = pages.find((p) => p.scenario === scenarioParam);
    if (target) {
      setSelectedId(target.id);
    } else {
      toast('该 App 还没有对应场景的支付页 · 请先「新建支付页面」，再配置渠道 placement', {
        tone: 'warn',
        icon: 'IconAlert',
      });
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('scenario');
        return next;
      },
      { replace: true },
    );
  }, [scenarioParam, isNativeApp, pagesLoaded, pages, setSearchParams]);
  const priorAppId = useRef(showApp.id);
  useEffect(() => {
    if (priorAppId.current === showApp.id) return;
    priorAppId.current = showApp.id;
    setNewPaywallOpen(false);
    setDetailDirty(false);
    setPendingLeave(null);
  }, [showApp.id]);
  // 路由级离开守卫（对照设计 window.__navGuard / app.jsx goto）：guardedLeave 只拦页内 tab / 左列表切换，
  // 侧栏点其它菜单等 react-router 路由离开也要拦 dirty 草稿 → useBlocker，blocked 时复用同款确认弹窗。
  const blocker = useBlocker(detailDirty && isNativeApp);
  // ConfirmDialog 确认后会同步再调 onClose —— 标记「已 proceed」避免 proceed 后又 reset 取消掉刚放行的导航
  const blockerProceededRef = useRef(false);

  // 整页 App 选择门：全局 'all' 时不静默落到首个 App，改为让运营显式选一个
  // （支付管理按 App 独立配置、无聚合视图）。onPick 切**全局** app-scope（setAppId），
  // 顶栏 AppSwitcher 与本页共用唯一真值源、进入下方详情/tab。
  // 早返回放在所有 hook 之后，不破坏 hooks 调用顺序。
  if (needGate) {
    return <PaywallAppGate apps={realApps} onPick={(id) => setAppId(id)} />;
  }
  if (!isWebApp && !isNativeApp) {
    const lookup = !isAll && appsQuery.isSuccess ? selectedAppQuery : appsQuery;
    const missing =
      lookup.error instanceof AdminApiError && lookup.error.code === 'RESOURCE_NOT_FOUND';
    const empty = isAll && appsQuery.isSuccess && realApps.length === 0;
    return (
      <div className="page page-enter">
        <div className="page-header">
          <h1 className="page-title">支付管理</h1>
        </div>
        <div className="card">
          {lookup.isError ? (
            <div role="alert">
              <p>{missing ? '所选应用不存在或已删除，请重新选择应用。' : lookup.error?.message}</p>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  if (missing) setAppId('all');
                  else void lookup.refetch();
                }}
              >
                {missing ? '选择应用' : '重试'}
              </button>
            </div>
          ) : empty ? (
            <p>暂无可管理的应用，请先创建 App。</p>
          ) : lookup.isSuccess ? (
            <p role="alert">无法识别应用平台，请在应用管理中检查配置。</p>
          ) : (
            <p role="status">正在读取应用平台…</p>
          )}
        </div>
      </div>
    );
  }

  // 注：不做整页骨架早返回 —— 页头 / App 切换 / 打开 Adapty / 新建 / tab 栏永远渲染，
  // 避免某条数据查询慢/挂时把整页 chrome（含可切换的「支付产品」tab）一起锁死。
  // 「支付产品」tab 走独立 useProductMetadata，不受 pagesLoading 影响；骨架只在「支付页面」
  // tab 的数据区内按 pagesLoading 局部显示（见下方 view==='pages' 分支）。

  return (
    <div className="page page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">支付管理</h1>
          <div className="page-sub">
            {isWebApp ? (
              '维护 Web 金币包与 VIP 商品 · 价格与币种由 Stripe 核验 · 默认与设备覆盖整份配置'
            ) : isNativeApp ? (
              <>
                按 App 维护支付页面（含各渠道 placement）· Adapty product_id / 价格 / 订阅周期由{' '}
                <span className="mono">Adapty</span> 承接 · COIN / BONUS 等业务效果在本后台维护
              </>
            ) : (
              '正在读取应用平台…'
            )}
          </div>
        </div>
        {isNativeApp && (
          <div className="hstack" style={{ gap: 8 }}>
            <button
              className="btn"
              onClick={() => {
                // 外链跳转 = 纯前端 window.open 静态 URL，无需 SSO；对齐设计稿 paywall.jsx 打开 Adapty
                window.open('https://app.adapty.io/', '_blank', 'noopener,noreferrer');
                toast('正在打开 Adapty 控制台', { tone: 'info', icon: 'IconExternal' });
              }}
            >
              <Icons.IconExternal size={12} />
              打开 Adapty
            </button>
            {activeView === 'pages' && (
              <button
                className="btn btn-accent"
                disabled={!canWrite}
                title={canWrite ? undefined : '权限不足（仅 root/admin/operator 可新建）'}
                onClick={() => setNewPaywallOpen(true)}
              >
                <Icons.IconPlus size={13} />
                新建支付页面
              </button>
            )}
          </div>
        )}
      </div>

      {/* View tabs */}
      <div style={{ borderBottom: '1px solid var(--border)', display: 'flex', gap: 0 }}>
        {[
          { id: 'pages', label: '支付页面', icon: 'IconWallet' as const },
          { id: 'products', label: '支付产品', icon: 'IconCoin' as const },
          { id: 'offers', label: '限时优惠 / Offer', icon: 'IconFlame' as const },
        ]
          .filter((t) => !isWebApp || t.id === 'products')
          .map((t) => (
            <button
              key={t.id}
              onClick={() => {
                if (t.id !== activeView) guardedLeave(() => setView(t.id));
              }}
              style={{
                border: 0,
                background: 'transparent',
                padding: '9px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                font: 'inherit',
                fontSize: 12.5,
                cursor: 'pointer',
                color: activeView === t.id ? 'var(--fg-strong)' : 'var(--fg-muted)',
                fontWeight: activeView === t.id ? 500 : 400,
                borderBottom:
                  activeView === t.id ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {createElement(Icons[t.icon], { size: 13 })} {t.label}
            </button>
          ))}
      </div>

      {activeView === 'products' &&
        (isWebApp ? (
          <WebPaymentProductsPanel key={showApp.id} app={showApp} />
        ) : isNativeApp ? (
          <PaywallProductsPanel app={showApp} />
        ) : null)}
      {isNativeApp && activeView === 'offers' && <OfferMirror app={showApp} />}
      {/* 「支付页面」tab 数据区：仅这块依赖 pagesLoading —— 在途时整块换骨架（与真实
          左列表 + 右详情双栏同形），页头 / tab 栏 / 工具按钮已在上方独立渲染不受影响。 */}
      {isNativeApp && activeView === 'pages' && pagesLoading && (
        <div style={{ display: 'grid', gridTemplateColumns: '300px minmax(0, 1fr)', gap: 14 }}>
          {/* Left: 页面列表骨架（标题 + 计数 + 若干列表行）*/}
          <div
            className="card"
            style={{
              padding: 6,
              alignSelf: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div
              style={{ padding: '8px 8px 6px', display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              <Sk w={140} h={11} />
              <Sk w={100} h={9} />
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                style={{ padding: '10px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                <div className="hstack" style={{ gap: 8 }}>
                  <Sk w={13} h={13} r={3} />
                  <Sk w={'60%'} h={12} />
                </div>
                <Sk w={'80%'} h={9} />
              </div>
            ))}
          </div>
          {/* Right: 详情骨架（KPI/状态条 + 配置 section + 表现 section）*/}
          <div className="vstack" style={{ gap: 14 }}>
            <KpiSkeleton count={3} />
            <SectionSkeleton rows={3} />
            <SectionSkeleton rows={2} />
          </div>
        </div>
      )}
      {isNativeApp && activeView === 'pages' && !pagesLoading && (
        <div style={{ display: 'grid', gridTemplateColumns: '300px minmax(0, 1fr)', gap: 14 }}>
          {/* Left: page list */}
          <div
            className="card"
            style={{ padding: 6, alignSelf: 'flex-start', position: 'sticky', top: 4 }}
          >
            <div style={{ padding: '8px 8px 6px' }}>
              <div className="label">{showApp.name} 的支付页面</div>
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                {pages.length} 个 · 含 {systemCount} 个系统预置
              </div>
            </div>
            {/* 此分支仅在 !pagesLoading 渲染（在途时整块走上方骨架），故只需空态文案 */}
            {pages.length === 0 && (
              <div style={{ padding: '14px 10px', fontSize: 11.5, color: 'var(--fg-faint)' }}>
                该 App 暂无支付页面
              </div>
            )}
            {pages.map((p) => {
              const active = selected?.id === p.id;
              return (
                <div
                  key={p.id}
                  onClick={() => {
                    if (p.id !== selected?.id) guardedLeave(() => setSelectedId(p.id));
                  }}
                  style={{
                    padding: '10px 10px',
                    borderRadius: 6,
                    marginBottom: 2,
                    cursor: 'pointer',
                    background: active ? 'var(--accent-soft)' : 'transparent',
                    position: 'relative',
                  }}
                >
                  {active && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        bottom: 8,
                        width: 2,
                        background: 'var(--accent)',
                        borderRadius: 2,
                      }}
                    />
                  )}
                  <div className="hstack" style={{ gap: 8 }}>
                    <Icons.IconWallet
                      size={13}
                      style={{ color: active ? 'var(--accent)' : 'var(--fg-muted)' }}
                    />
                    <span style={{ flex: 1, fontWeight: active ? 500 : 400, fontSize: 12.5 }}>
                      {p.name}
                    </span>
                    {p.system && <Icons.IconLock size={9} style={{ color: 'var(--fg-faint)' }} />}
                    <span
                      className={`chip ${p.status === '上架' ? 'chip-pos' : 'chip-warn'}`}
                      style={{ fontSize: 9.5, padding: '0 5px' }}
                    >
                      {p.status}
                    </span>
                  </div>
                  <div
                    className="mono"
                    style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 2 }}
                  >
                    {p.scenario} · {p.channels.length} 渠道
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: page detail */}
          {selected && (
            <PaywallPageDetail
              key={selected.id}
              page={selected}
              app={showApp}
              canWrite={canWrite}
              canDelete={canDelete}
              onDirtyChange={setDetailDirty}
            />
          )}
        </div>
      )}

      {isNativeApp && newPaywallOpen && (
        <NewPaywallPageDialog
          app={showApp}
          apps={apps}
          existingScenarios={new Set(pages.map((p) => p.scenario))}
          onClose={() => setNewPaywallOpen(false)}
        />
      )}

      {/* 离开守卫：详情有未保存 placement 改动时切换页面 / tab → 弹确认（继续编辑 / 丢弃更改并离开），
          确认即执行被拦截的离开动作并清 dirty；取消 / ESC = 继续编辑、留在原页。 */}
      {isNativeApp && pendingLeave && (
        <ConfirmDialog
          title="有未保存的更改"
          message={`「${selected?.name ?? '当前支付页面'}」有未保存的草稿改动。离开将放弃这些改动；渠道启停与页面上下架状态不受影响。`}
          tone="warn"
          icon="IconAlert"
          confirmLabel="放弃修改并离开"
          cancelLabel="继续编辑"
          onConfirm={() => {
            toast('已放弃未保存的草稿改动', { tone: 'warn' });
            pendingLeave();
            setDetailDirty(false);
          }}
          onClose={() => setPendingLeave(null)}
        />
      )}

      {/* 路由级离开守卫弹窗：侧栏 / 跨屏路由跳转命中 useBlocker → 复用同款「有未保存的更改」确认；
          丢弃更改并离开 = blocker.proceed()（放行导航），继续编辑 / ESC = blocker.reset()（留在本页）。 */}
      {blocker.state === 'blocked' && (
        <ConfirmDialog
          title="有未保存的更改"
          message={`「${selected?.name ?? '当前支付页面'}」有未保存的草稿改动。离开将放弃这些改动；渠道启停与页面上下架状态不受影响。`}
          tone="warn"
          icon="IconAlert"
          confirmLabel="放弃修改并离开"
          cancelLabel="继续编辑"
          onConfirm={() => {
            toast('已放弃未保存的草稿改动', { tone: 'warn' });
            blockerProceededRef.current = true;
            setDetailDirty(false);
            blocker.proceed();
          }}
          onClose={() => {
            // 确认路径已 proceed —— 跳过 reset，避免取消掉刚放行的导航
            if (blockerProceededRef.current) {
              blockerProceededRef.current = false;
              return;
            }
            blocker.reset();
          }}
        />
      )}
    </div>
  );
}

/** 支付页面配置变更历史抽屉（ADM-PAY，复用 ADM-BOK-S015 日志模式）：GET /audit-logs?resource=payment_pages&resource_id */
function PaywallAuditDrawer({
  pageId,
  pageName,
  onClose,
}: {
  pageId: string;
  pageName: string;
  onClose: () => void;
}) {
  const auditQc = useQueryClient();
  // 本页的变更分散在**两个 resource** 下：页面自身配置走 `payment_pages`，商品展示顺序
  // （ADM-PAY-S009）走 `app_payment_product_orders`（resourceId 同样是 payment_pages.id）。
  // 只查前者会让运营刚保存的排序在「操作日志」里完全不可见 —— 排查排序异常时无迹可循。
  const pageLogs = useAuditLogsPage({
    page: 1,
    limit: 50,
    filters: { resource: 'payment_pages', resource_id: pageId },
  });
  const orderLogs = useAuditLogsPage({
    page: 1,
    limit: 50,
    filters: { resource: 'app_payment_product_orders', resource_id: pageId },
  });
  const isLoading = pageLogs.isLoading || orderLogs.isLoading;
  // 任一路失败都必须显式呈现：hook 失败时返空数组、isLoading 也变 false，
  // 于是抽屉会把「只查到一半」或「一条都没查到」渲染成「暂无变更记录」——
  // 把查询故障伪装成「确实没有变更过」，排查排序异常时会被这条假信息带偏。
  //
  // **不能只判 `isError && 无数据`**：TanStack Query 在**刷新失败时保留旧缓存**，于是
  // 「保存后 invalidate → refetch 失败」会得到 isError=true 且 data 非空 —— 抽屉会安静地
  // 显示一份缺了刚保存那条的旧列表，而 P0-admin-audit-log §5 要求这个抽屉呈现该资源的
  // **全部** audit。故按三态分开：完全无数据 → 错误态；有旧数据 → 照常渲染但顶部标明陈旧。
  const anyError = pageLogs.isError === true || orderLogs.isError === true;
  // 两路各取最近 50 条后按时间倒序归并，再截回 50：与单路时的「最近 50 条」语义一致。
  // 按 id 去重：两路的 resource 不同、正常不会返回同一行，但合并列表直接进 key={l.id} 渲染，
  // 一旦有任何重复就是 React 重复 key（渲染紊乱），这里兜住比在渲染层崩掉便宜。
  const logs = [...new Map([...pageLogs.data, ...orderLogs.data].map((l) => [l.id, l])).values()]
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0))
    .slice(0, 50);
  const total = pageLogs.total + orderLogs.total;
  const updating = pageLogs.isServiceUpdating || orderLogs.isServiceUpdating;
  const loadFailed = updating || (anyError && logs.length === 0);
  const staleWarning = anyError && logs.length > 0;
  return (
    <Drawer
      title={`操作日志 · ${pageName}`}
      subtitle="该支付页面的配置变更历史（操作人 / 时间 / 动作）"
      icon="IconHistory"
      onClose={onClose}
    >
      {isLoading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 12.5 }}>
          加载中…
        </div>
      ) : loadFailed ? (
        <div style={{ padding: 24, textAlign: 'center', fontSize: 12.5 }}>
          <div style={{ color: 'var(--fg-neg)' }}>
            {updating ? '服务更新中' : '变更记录加载失败'}
          </div>
          <div style={{ color: 'var(--fg-muted)', marginTop: 6 }}>
            这不代表「没有变更」——请重试后再判断。
          </div>
          {/* OffsetPageView 不暴露 refetch，故走 queryClient 失效整个 audit-logs 域重取 */}
          <button
            className="btn btn-sm"
            style={{ marginTop: 10 }}
            onClick={() => {
              void auditQc.invalidateQueries({ queryKey: ['audit-logs'] });
            }}
          >
            重试
          </button>
        </div>
      ) : logs.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 12.5 }}>
          暂无变更记录
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {staleWarning && (
            // 刷新失败但仍有旧缓存：数据不丢，但必须标明它可能不含最新一次变更，
            // 否则运营会拿一份缺条目的列表当成完整历史
            <div
              style={{
                padding: '8px 12px',
                fontSize: 12,
                color: 'var(--fg-neg)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              刷新失败，以下为缓存记录，可能不含最新一次变更。
              <button
                className="btn btn-sm"
                style={{ marginLeft: 8 }}
                onClick={() => {
                  void auditQc.invalidateQueries({ queryKey: ['audit-logs'] });
                }}
              >
                重试
              </button>
            </div>
          )}
          <Table className="tbl">
            <thead>
              <tr>
                <th>时间</th>
                <th>动作</th>
                <th>操作人</th>
                <th className="num">状态</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="mono">{fmtDateTime(l.created_at)}</td>
                  <td>{l.action}</td>
                  <td className="mono">{l.actor_id}</td>
                  <td className="num mono">{l.status}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {total > logs.length && (
            <div style={{ padding: 8, fontSize: 11, color: 'var(--fg-faint)' }}>
              显示最近 {logs.length} / 共 {total} 条
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function PaywallPageDetail({
  page,
  app,
  canWrite,
  canDelete,
  onDirtyChange,
}: {
  page: PaywallPageItem;
  app: ShowApp;
  canWrite: boolean;
  canDelete: boolean;
  // 上报本详情是否有未保存的 placement 改动，供父级「切换页面 / 切 tab」前的离开守卫
  onDirtyChange: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();

  // Adapty placement 候选（catalog 同步）；未配置/上游故障 → isError，退到手输兜底 + 引导去集成页
  const adaptyCatalog = useAdaptyCatalog(app.id);
  const placementOptions = adaptyCatalog.data?.placements ?? [];

  // 通道启停真值 = 全局通道策略组的成员集合（ADM-PAY-S008，A8 §7.8.0「全局策略组的成员集合
  // 就是该 App 的全局启停」），维护入口在「App 详情 → 支付渠道 → 支付通道策略」。
  // 本页据它决定 servable 口径与「唯一可用渠道 → 一并下架页面」确认弹窗：判据见下方 servesAnywhere
  // （全局组 ∪ 任一覆盖组）。只配了 placement 但两者都不含的渠道，C 端一条都不返回。
  //
  // **未知 ≠ 已关闭**：策略组未就绪（loading / 失败）时一律按「不主张它关着」处理，否则会渲染出
  // 与事实相反的结论、并误弹「唯一可用渠道」确认。所有启停判断都必须经 servesAnywhere。
  const policies = usePaymentMethodPolicies(app.id);
  // **只有 loading / 失败才算「未知」**。请求成功但没有全局组是一个**已知**事实，且 serving 后果
  // 最重：契约 `02-API契约/60-支付与订阅.md` §1「全局策略组本身缺失 → 按空策略 fail-closed 返空态」，
  // 本仓 server 侧 `apps-admin-service.ts` 的 listPolicyMembersForReadingPassGate 同口径。
  // 把它并进「未知」会让本页对「该 App 一个通道都不返回」显示成「全通道可用」。
  // 成员 → 结算通道取 §7.8.8 可选列表的 `channel`；列表没拿到同样算「未知」，不拿本地映射去猜
  const paymentMethodOptions = usePaymentMethodOptions(app.id);
  const policiesKnown = !policies.isLoading && !policies.isError && paymentMethodOptions.isSuccess;
  const memberChannelOf = createPaymentMethodOptionLookup(paymentMethodOptions.data).channelOf;
  const globalPolicyMembers =
    policies.data.find((p) => p.region_code === null)?.payment_methods ?? [];
  const regionPolicyGroups = policies.data.filter((p) => p.region_code !== null);
  /**
   * 本页的渠道 id（`PAYWALL_CHANNEL_IDS`：`iap` / `stripe` / `payermax`）是**结算通道**，而策略成员是**支付方式**
   * （`03-数据字典/70-产品配置.md` §3「两层闭集」）—— 比对前必须先按 §7.8.8 `channel` 投影；
   * 不在可选列表的成员（已退役 / WEB App 的存量 IAP）C 端不下发，投影为 null、不算任何渠道的成员。
   *
   * **不能字面比 `'STRIPE'`**：srv 0200 把存量 `STRIPE` 成员原位展开成三个 `STRIPE_*` 并收紧了
   * CHECK，成员里根本不存在 `STRIPE`，字面比恒不命中。后果是 Stripe 渠道恒挂「不在通道策略内」
   * 的相反结论，且关 IAP 时 `remainingServable` 被算成 0 → 弹「这是唯一可用渠道」的双动作确认 →
   * 运营点「一并下架页面」会触发 A8 §7.3 下架级联，清空该 scenario **全部**渠道行的 placement。
   * 同一投影见 `payment-channel-readiness-adapter.ts` 的 `checkPolicy` / `channelEnabledInAnyPolicy`。
   */
  const hasChannelMember = (members: { payment_method: string }[], chId: string) =>
    members.some(
      (m) => memberChannelOf(m.payment_method) === settlementChannelOfPaywallChannelId(chId),
    );
  const inGlobalPolicy = (chId: string) => hasChannelMember(globalPolicyMembers, chId);
  /** 含该通道的 Region 覆盖组（契约 ①：覆盖组能让全局组没有的通道在该地区可用）。 */
  const regionsServing = (chId: string) =>
    regionPolicyGroups
      .filter((g) => hasChannelMember(g.payment_methods, chId))
      .map((g) => g.region_code as string);
  /** 排除该通道的 Region 覆盖组（契约 ②：覆盖组是整套替换，能让全局组有的通道在该地区不可用）。 */
  const regionsExcluding = (chId: string) =>
    regionPolicyGroups
      .filter((g) => !hasChannelMember(g.payment_methods, chId))
      .map((g) => g.region_code as string);
  /**
   * 「至少一部分请求拿得到」——未知态一律返 true（不主张它关着）。
   *
   * 判据是**并集**而非逐 App 运营地区：serving 的地区键是 `effective_store_region`，其值域为
   * `system_regions` **完整预置清单 ∪ NULL**，与 `apps.region_codes` 无关 ——
   * `01-通用约定/80-请求上下文.md` §3.0「归一后的值域 = `system_regions.region_code` **完整预置
   * 清单**…与 `app_payment_method_policies.region_code` 同编码（alpha-2）精确匹配」，同节末
   * 「**不推导其他业务维度**：业务地域仍以 `X-App-Id` → `apps.region_codes` 为准」把两者显式分开
   * （该文件 §2 的 `X-Region` 行另有一句「**不**取 App 运营市场配置」，说的是 header 取值来源）。
   * 且同节明写
   * 「`X-Store-Region` 缺失 / 归一失败 / 不在预置清单、IP 兜底不可用、或命中地区无覆盖组 →
   * **一律回退该 App 的全局策略**」—— 所以只要全局组含该通道，就**必然**有一部分请求拿得到它。
   * 把判据收窄到 `app.market` 会把「拿得到」判成「拿不到」，进而误弹「一并下架页面」。
   */
  const servesAnywhere = (chId: string) =>
    !policiesKnown || inGlobalPolicy(chId) || regionsServing(chId).length > 0;

  // 渠道：page.channels（已配置渠道）；详情页按结算通道闭集逐个可配 placement，默认选已配置首个
  const [channel, setChannel] = useState(page.channels[0] ?? PAYWALL_CHANNEL_IDS[0] ?? 'iap');

  // 命名示例（**仅作 input placeholder 文案**，非校验依据、绝不作为保存值）：pw_{app}_{scenario}_{channel}
  const expectedPl = (ch: string) => `pw_${app.id}_${page.scenario}_${ch}`;
  // per-channel placement 存储：初始取真值；**未配置回显空串**——绝不用命名约定伪造一个 Adapty 上不存在的
  // slug 当初值（否则「未配置」会被保存按钮静默写成假 placement，C 端按它拉 Paywall 必空）。
  const [placements, setPlacements] = useState<Record<string, string>>(() =>
    placementDraftOf(page.placements),
  );
  const placement = placements[channel] ?? '';
  const setPlacement = (v: string) => setPlacements((p) => ({ ...p, [channel]: v }));
  // 「未保存更改」判定：当前 per-channel placement 与进入本页时的已保存基线不一致（用户改了 Select / 手输但未保存）。
  // 上报父级用于「切换支付页面 / 切 tab」前的离开守卫；卸载时清零，避免父级残留 dirty 误触确认。
  const dirty = PAYWALL_CHANNEL_IDS.some(
    (ch) => (placements[ch] ?? '') !== (page.placements[ch] ?? ''),
  );
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  // placement 选择：list 模式（自研 Select 富选项：name 主名 + mono id + kind chip）/ manual 模式
  // （手输兜底）per-channel 切换，对照设计稿 paywall.jsx manualPl。无 catalog 候选时强制 manual（无可选项）。
  const [manualPl, setManualPl] = useState<Record<string, boolean>>({});
  const isManual = (manualPl[channel] ?? false) || placementOptions.length === 0;
  // 当前 placement 值是否在 catalog 列表中（绿色「已同步」条仅在真存在时给，不编造已同步）
  const plInList = placementOptions.some((p) => p.placement_id === placement);
  // 选中 placement 的 paywall 下产品（catalog 富集，server 按 paywall_name↔title 派生）；旧缓存无 products → 兜 []
  const placementProducts =
    placementOptions.find((p) => p.placement_id === placement)?.products ?? [];
  // placement 校验基于「真 Adapty 同步存在」plInList（catalog 有该 placement）而非命名约定（约定非 dev-docs 契约、
  // 仅默认占位）。状态卡 ③④ 与渠道启用 guard 直接用 plInList / placementOptions，不再派生 plUsable 等门控变量
  // （保存按钮已随设计稿改单按钮草稿存、不 gate Adapty 同步态；上架前的同步态由渠道启用 guard 与状态卡兜底）。
  // catalog 最近同步时刻（DTO synced_at）→ 绿色确认条展示「最近同步 {lastSync}」
  const lastSync = adaptyCatalog.data?.synced_at ? fmtDateTime(adaptyCatalog.data.synced_at) : '—';
  // 同步 placement 列表：fetchQuery + refresh=true 旁路服务端 KV 缓存强制从 Adapty 重拉（对照设计 doSyncPl）。
  // 进行中靠 spinner 按钮 + 状态卡片表达（不弹「进行中」toast，避免连点堆叠）；完成才弹结果 toast。
  const [plSyncing, setPlSyncing] = useState(false);
  const doSyncPl = () => {
    if (plSyncing) return; // 防重入：同步进行中再点不重复触发（避免 toast 堆叠）
    setPlSyncing(true);
    queryClient
      .fetchQuery({
        queryKey: ['adapty', 'catalog', app.id],
        queryFn: () => adaptyApi.catalog(app.id, true),
        // staleTime:0 覆盖全局默认 30s——否则 fetchQuery 在 30s 内直接返缓存、不调 queryFn（= 不真打 Adapty、
        // 瞬间假成功、看不到「同步中」）。强制每次点击都真重拉（queryFn 的 refresh=true 再旁路服务端 KV 缓存）。
        staleTime: 0,
      })
      .then((data) => {
        const n = data?.placements?.length ?? 0;
        // 成功有结果 → pos；成功但 0 个（Adapty 该 App 没建 placement）→ warn 引导去 Adapty 创建
        toast(
          n > 0
            ? `已从 Adapty 同步 placement 列表 · 共 ${n} 个`
            : '已同步 · 该 App 的 Adapty 账户暂无 placement（请先去 Adapty 后台创建）',
          { tone: n > 0 ? 'pos' : 'warn', icon: n > 0 ? 'IconRefresh' : 'IconAlert' },
        );
      })
      .catch((err) => {
        // 未配 Key / 上游故障 → 明确报错 toast（之前静默吞 → 用户看不到任何完成提示）
        toast(
          err instanceof Error
            ? err.message
            : '同步失败 · 请确认该 App 已在「集成」配置 Adapty Key',
          {
            tone: 'neg',
            icon: 'IconAlert',
          },
        );
      })
      .finally(() => setPlSyncing(false));
  };

  // 草稿保存驻留指示（照设计稿 draftSavedAt）：保存成功后页脚显示「草稿已保存 · 刚刚」，再改动被 dirty 态覆盖
  const [savedAtLabel, setSavedAtLabel] = useState<string | null>(null);

  // 下架二次确认弹窗 + 删除弹窗本地态
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  // 存量矛盾页（显示下架却仍有 placement）的收敛入口：下架态 header 只渲染「页面上架」，
  // 没有可再点一次的「页面下架」，光靠 A8 §7.3「一律执行清空」在 UI 上够不着。
  const [clearResidualOpen, setClearResidualOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [productOrderOpen, setProductOrderOpen] = useState(false);

  // 保存配置（草稿，单按钮·随设计稿）：upsertPaywall 当前渠道 placement_id。渠道行已无启停开关
  // （dev-docs #530）：**空串保存为 null = 清空该渠道配置**，不动页面上下架（走 header togglePageMutation）。
  const saveMutation = useMutation({
    mutationFn: async () => {
      const value = placement.trim() === '' ? null : placement;
      await paymentPagesApi.upsertPaywall(page.id, {
        ...channelRowKeyOf(channel),
        adapty_placement_id: value,
        ...(page.placementsToken ? { expected_updated_at: page.placementsToken } : {}),
      });
      if (value !== null) syncPopupSiblingDrafts(channel, value);
      return value;
    },
    onSuccess: async (value) => {
      setSavedAtLabel('刚刚');
      setStaleConflict(false);
      // 弹窗页按实际结果说：清空不连带；写入成功且其它渠道已配置 = 这些渠道现在同值（原本同值或被连带同步）。
      const otherFilled = PAYWALL_CHANNEL_IDS.some((ch) => ch !== channel && page.placements[ch]);
      toast(
        !isPopupPage
          ? '配置已保存（草稿）· 页面上下架状态未变更'
          : value === null
            ? '已清空该渠道的 placement · 其它渠道不受影响'
            : otherFilled
              ? '配置已保存 · 其它已配置渠道与之共用同一个 placement'
              : '配置已保存',
        { tone: 'pos', icon: 'IconCheck' },
      );
      await invalidatePaywallQueries();
    },
    onError: async (err) => {
      const stale = staleSnapshotErrorMessage(err, '渠道配置');
      // 陈旧快照**不**刷新：刷新会换上新令牌而输入框仍是本地草稿，再点保存就以新令牌静默覆盖对方的改动。
      // 改为常驻冲突标记，由运营点「载入最新配置」把草稿与令牌一起换成最新值。
      if (stale) setStaleConflict(true);
      toast(stale ?? (err instanceof Error ? err.message : '保存失败'), {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  // 页面上下架（header 上架 / 下架按钮）：update(is_active)
  const togglePageMutation = useMutation({
    mutationFn: async (next: boolean) => {
      await paymentPagesApi.update(page.id, { is_active: next });
      // 下架级联会清空本页全部 placement（A8 §7.3）→ **本地输入态必须跟着清**：
      // 不清则 ① 输入框仍显示已被清掉的值，运营看不出清空是否生效；② dirty 恒真，
      // 切页/离开一律被「未保存的更改」拦，而「保存配置」此刻又被下架态禁用、消不掉；
      // ③ 再点上架解禁后按「保存配置」会把刚清空的 placement 原样写回，清空被静默撤销。
      // operational_popup 不走级联（页级 is_active 对它无执行点），故不动它的本地态。
      if (!next && page.scenario !== 'operational_popup') {
        setPlacements(placementDraftOf({}));
      }
    },
    onSuccess: async (_dto, next) => {
      toast(
        next
          ? `已上架「${page.name}」`
          : page.status === '上架'
            ? `已下架「${page.name}」`
            : `已清空「${page.name}」的残留渠道配置`,
        {
          tone: next ? 'pos' : 'warn',
          icon: next ? 'IconCheck' : 'IconPause',
        },
      );
      // 下架级联已清空本地草稿、下面又重取令牌 → 草稿与令牌同步为最新，冲突标记随之失效
      if (!next) setStaleConflict(false);
      await invalidatePaywallQueries();
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '操作失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  const writeDisabled = !canWrite || saveMutation.isPending || togglePageMutation.isPending;

  // 渠道写成功后要失效的读：本页列表、App 支付摘要，以及运营弹窗页的只读落点区（['popup-placements']，
  // 同一批 app_payment_methods 行；不失效则从运营弹窗页跳来配完再回去，30s staleTime 内仍显示旧落点）。
  async function invalidatePaywallQueries() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['payment-pages', app.id] }),
      queryClient.invalidateQueries({ queryKey: appQueryKeys.paymentMethods(app.id) }),
      queryClient.invalidateQueries({ queryKey: ['popup-placements', app.id] }),
    ]);
  }

  // 陈旧快照冲突（RESOURCE_CONFLICT + STALE_SNAPSHOT）后置位，直到运营载入最新配置或下一次保存成功。
  // 冲突时刻意不刷新数据（见 saveMutation.onError），代价是界面停在旧值，故需要常驻标记而不只是 6s toast。
  const [staleConflict, setStaleConflict] = useState(false);
  const [reloadingLatest, setReloadingLatest] = useState(false);
  // 载入最新配置：重取本页数据，再把各渠道草稿与令牌一起换成服务端最新值（只换数据、不换令牌会让下一次
  // 保存以新令牌覆盖对方）。读的是重取后的缓存，不等 props 下一轮渲染。
  const reloadLatest = async () => {
    const key = ['payment-pages', app.id];
    const loadedBefore = queryClient.getQueryState(key)?.dataUpdatedAt ?? 0;
    setReloadingLatest(true);
    try {
      await invalidatePaywallQueries();
      // invalidateQueries 在重取失败时不抛：必须核实真的拿到了新数据，否则会拿旧缓存重置草稿、
      // 清掉冲突标记，而令牌仍是旧的 —— 运营以为已载入最新，下一次保存照旧冲突。
      const state = queryClient.getQueryState(key);
      if (!state || state.status === 'error' || state.dataUpdatedAt <= loadedBefore) {
        toast('载入最新配置失败 · 请稍后重试或刷新页面', { tone: 'neg', icon: 'IconAlert' });
        return;
      }
      const latest = queryClient
        .getQueryData<PaywallPageItem[]>(key)
        ?.find((p) => p.id === page.id);
      if (!latest) {
        // 本页已被他人删除：列表随之切到其它页、本详情卸载；这里只给出原因
        toast('该支付页已被删除 · 已载入最新列表', { tone: 'warn', icon: 'IconAlert' });
        return;
      }
      if (latest.paywallsError) {
        // 列表重取成功但本页渠道明细没取到：placements 不可信，保留草稿与冲突标记
        toast('载入最新配置失败 · 请稍后重试或刷新页面', { tone: 'neg', icon: 'IconAlert' });
        return;
      }
      setPlacements(placementDraftOf(latest.placements));
      setStaleConflict(false);
    } finally {
      setReloadingLatest(false);
    }
  };
  // F5：该页 listPaywalls 拉取失败 → channels/placements 不可信，禁用 placement 写 / 保存 / 上架，
  // 避免运营在残缺数据上 publish 覆盖真实配置（仅显式成功才放行写）。
  const configDisabled = writeDisabled || page.paywallsError;
  // 下架页不可写 placement（后端 A8 §7.5 ⑤ 抛 PAY_PAGE_INACTIVE）——前端同判据先禁用，免得运营
  // 填完才吃一个内部错误码。**只 gate placement 写侧**：上架按钮与渠道启停仍走 configDisabled，
  // 否则下架页既配不了 placement、也上不了架 = 死锁。
  // operational_popup 例外（A8 §7.5「operational_popup 分支」）：本页是该 scenario 落点的唯一写入口，
  // 页级上下架对它无执行点（弹窗用不用由运营弹窗场景上下架决定），故不受上架态约束、也不渲染上下架入口。
  const isPopupPage = page.scenario === 'operational_popup';
  // 陈旧冲突期间一律禁写：冲突时不刷新是为保住「草稿 ↔ 令牌」的对应，但其它入口（新建 / 删除别的页、
  // 重连 refetch）照样会把令牌换新而草稿不变，此时放行保存就以新令牌静默覆盖对方。只能经「载入最新配置」解禁。
  const placementWriteDisabled =
    configDisabled || staleConflict || reloadingLatest || (!isPopupPage && page.status !== '上架');

  // operational_popup 单 placement（A8 §7.5）：给某渠道写入非空值时，服务端把其它**已填值**渠道连带同步为
  // 同值。本地输入态必须跟着改，否则另一渠道输入框留着旧值、dirty 恒真，再点保存会把旧值写回、又连带
  // 覆盖刚保存的这一个。只同步已存值非空的渠道（服务端不填未配置的渠道）。
  const syncPopupSiblingDrafts = (savedChannel: string, value: string) => {
    if (!isPopupPage) return;
    setPlacements((prev) => {
      const next = { ...prev };
      for (const ch of PAYWALL_CHANNEL_IDS) {
        if (ch !== savedChannel && page.placements[ch]) next[ch] = value;
      }
      return next;
    });
  };

  // —— 渠道配置 / 清空（与页面上下架解耦）——
  // enabled：派生自 page.channels（已配置 placement 的渠道，真值源）；用于「唯一可用渠道」判断
  const enabled: Record<string, boolean> = Object.fromEntries(
    PAYWALL_CHANNEL_IDS.map((ch) => [ch, page.channels.includes(ch)]),
  );
  // 框架通道（如 PayerMax）还要本 App 的通道配置就绪 C 端才下发（字典 65 §1 收单前置）。本页只对原生 App
  // 渲染，故按原生 App 判回跳地址。读不到 = 未知，与策略组同口径「未知 ≠ 已关闭」：不据此判不可 serve，
  // 只在卡片上标「无法判定」。
  const acquiringOf = useAcquiringPrerequisiteStates(app.id, { isNativeApp: true });
  const acquiringStateOf = (chId: string) => acquiringOf(chId.toUpperCase());
  // 「可 serve」口径：已配 placement、该通道在策略组成员里、框架通道另须收单前置满足，才真对客户端可用。
  const servable = (id: string) =>
    (enabled[id] ?? false) && servesAnywhere(id) && acquiringStateOf(id).problem === null;
  // swNonce：Switch 点击会翻自身内部态；弹窗取消时无 prop 变化、不会自动回弹 → 自增 nonce 改 key 强制重挂回弹
  const [swNonce, setSwNonce] = useState(0);
  // 渠道切换二次确认（双动作）本地态：null=未弹，对象=弹（含两动作回调）
  const [chConfirm, setChConfirm] = useState<null | {
    title: string;
    message: string;
    // 补充要点（弹窗 message 下 <ul>，如「渠道 · Stripe」「placement · pw_xxx」，照设计稿 details）
    details?: string[];
    tone: 'default' | 'warn';
    confirmLabel: string;
    // ReactNode：设计稿在「仍保持上架」用 warn 色 span 强调
    secondaryLabel: ReactNode;
    onConfirm: () => void;
    onSecondary: () => void;
  }>(null);
  // 渠道展示名（卡片 / toast / 弹窗文案用），与 App 支付渠道 tab 同源
  const chName = (id: string) => channelDisplayLabel(id.toUpperCase());

  // 渠道配置 / 清空接真：渠道行无启停开关，"启用"= 写入 placement、"停用"= 置 null 清空配置（dev-docs #530）；
  // 可选页面 is_active 经 update（alsoPage 传入才动页面）。
  const channelToggleMutation = useMutation({
    mutationFn: async ({
      chId,
      enable,
      alsoPage,
    }: {
      chId: string;
      enable: boolean;
      /** 仅 false 有意义 = 停用最后一个可 serve 渠道时「一并下架页面」；上架方向已无此路径（见 requestToggle ②）。 */
      alsoPage?: false;
    }) => {
      await paymentPagesApi.upsertPaywall(page.id, {
        ...channelRowKeyOf(chId),
        // 清空走 null；启用写当前输入值（调用方已保证非空，见 requestToggle ①）
        adapty_placement_id: enable ? (placements[chId] ?? '') : null,
        ...(page.placementsToken ? { expected_updated_at: page.placementsToken } : {}),
      });
      if (enable) syncPopupSiblingDrafts(chId, placements[chId] ?? '');
      // 本地 placement 态紧跟**已落地**的写入结果，而不是等 onSuccess——下面的页面上下架 PATCH 若失败，
      // 整个 mutation 走 onError、onSuccess 不执行，清空就不会同步到输入框：旧值仍在、立即变 dirty，
      // 用户再点「保存配置」会把刚清空的 placement 原样写回（清空被静默撤销）。
      if (!enable) setPlacements((p) => ({ ...p, [chId]: '' }));
      if (alsoPage === false) {
        await paymentPagesApi.update(page.id, { is_active: false });
        // 下架级联清的是该 scenario **全部**渠道行、不只当前这个（A8 §7.3）→ 本地态同样要全清。
        // 只清 [chId] 会让另一渠道的输入框留着已被后端抹掉的值：dirty 恒真消不掉，且再上架后
        // 在那个渠道点「保存配置」会把清空静默写回。与 togglePageMutation 同一根因的第三个面。
        if (page.scenario !== 'operational_popup') {
          setPlacements(placementDraftOf({}));
        }
      }
      return { chId, enable, alsoPage };
    },
    onSuccess: async (data) => {
      setStaleConflict(false);
      await invalidatePaywallQueries();
      // 成功 toast 移到写入真正落地后弹（含弹窗「一并上架/下架」路径），避免 mutate() 后同步弹「假成功」
      toast(
        data.enable ? `已启用 ${chName(data.chId)} 渠道` : `已停用 ${chName(data.chId)} 渠道`,
        data.enable ? { tone: 'pos', icon: 'IconCheck' } : { tone: 'warn', icon: 'IconPause' },
      );
    },
    onError: async (err) => {
      const stale = staleSnapshotErrorMessage(err, '渠道配置');
      if (stale) {
        // 陈旧快照：服务端在任何写之前拒绝，无半提交；不刷新（理由同 saveMutation.onError）
        setStaleConflict(true);
      } else {
        // 其余失败（含两步写半提交：渠道已翻、页面未变）→ 重新拉真态，避免 UI 停在 mutation 前的旧 page
        await invalidatePaywallQueries();
      }
      toast(stale ?? (err instanceof Error ? err.message : '操作失败'), {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  // 渠道开关切换状态机（逐字照设计稿 requestToggle，toast/mutate 接真）
  const requestToggle = (chId: string, next: boolean) => {
    // 防御性再入闸：toggle 在途时不再触发（避免并发 upsertPaywall/update 叠写竞态）；
    // Switch 也按 isPending 禁用，双保险。
    if (channelToggleMutation.isPending) return;
    setSwNonce((n) => n + 1); // 先回弹到受控值；确认/直接执行后由 invalidate 重渲染落真态
    if (next) {
      // —— 启用渠道 ——
      // ① 仅当「catalog 已同步、但该渠道 placement 不在其中」（确知未配进 Adapty）才阻止启用——
      //    否则客户端按它拉 Paywall 会拉空。命名是否符约定不再拦（约定非 dev-docs 契约）；无 catalog 可校验时信任运营。
      const chPl = placements[chId] ?? '';
      // ⓪ placement 为空不能启用——渠道"已配置"的定义就是 placement 非空，空值保存等于清空。
      if (chPl.trim() === '') {
        toast(`无法启用 ${chName(chId)} · 请先填写该渠道的 Adapty placement`, {
          tone: 'warn',
          icon: 'IconBan',
        });
        return;
      }
      if (placementOptions.length > 0 && !placementOptions.some((p) => p.placement_id === chPl)) {
        toast(
          `无法启用 ${chName(chId)} · 该 placement 未在 Adapty 同步列表中找到 · 请先在 Adapty 创建并同步`,
          { tone: 'warn', icon: 'IconBan' },
        );
        return;
      }
      // ② 启用渠道（此处必为上架页）→ 直接启用、无弹窗。
      // 原「④ 页面下架 → 弹「一并上架页面」双动作」已删：下架页写 placement 会被后端拒
      // （A8 §7.5 ⑤ PAY_PAGE_INACTIVE），前端对应地把渠道开关按 placementWriteDisabled 禁用，
      // requestToggle 在下架页根本不会被触发 —— 那个分支不可达。正确路径是先用 header 上架、再配渠道。
      channelToggleMutation.mutate({ chId, enable: true });
      return; // 成功 toast 在 onSuccess 弹
    }
    // —— 停用渠道 ——
    // 清空后剩余「可 serve 通道」数（servable 口径：已配 placement、在全局组或任一覆盖组内、框架通道收单前置
    // 满足），按结算通道闭集逐个计（含已登记框架通道）
    const remainingServable = PAYWALL_CHANNEL_IDS.filter((c) => c !== chId && servable(c)).length;
    // ① 停用后无任何可 serve 通道且页面在上架 → 弹双动作确认（一并下架页面 / 仍保持上架）。
    // operational_popup 不弹：页级下架对它无执行点，「一并下架页面」清不掉落点；清空最后一个已配渠道时
    // 若仍有上架中的弹窗场景，服务端返 PAY_POPUP_PLACEMENT_REFERENCED，由 onError 给出指引。
    if (!isPopupPage && remainingServable === 0 && page.status === '上架') {
      setChConfirm({
        title: '这是当前唯一对客户端可用的支付渠道，停用后该支付页将无可用支付方式',
        message: `${chName(chId)} 是「${page.name}」当前唯一对客户端可用的支付渠道。停用后该支付页虽仍处于上架，但客户端将无任何可用支付方式。选「一并下架页面」会触发下架级联，清空本页全部渠道的 placement（含当前不可 serve 的渠道），清空后不可恢复、需逐渠道重填。`,
        details: [
          `渠道 · ${chName(chId)}`,
          ...(page.channels.length > 1
            ? [`一并下架将清空本页全部 ${page.channels.length} 个渠道的 placement`]
            : []),
        ],
        tone: 'warn',
        confirmLabel: '一并下架页面',
        secondaryLabel: (
          <span style={{ color: 'var(--warn)' }}>仍保持上架（页面将无可用支付）</span>
        ),
        onConfirm: () => channelToggleMutation.mutate({ chId, enable: false, alsoPage: false }),
        onSecondary: () => channelToggleMutation.mutate({ chId, enable: false }),
      });
      return;
    }
    // ② 停用后仍有可 serve 通道（或页面已下架）→ 直接停用、无弹窗
    channelToggleMutation.mutate({ chId, enable: false });
    // 成功 toast 在 onSuccess 弹
  };

  return (
    <div className="vstack" style={{ gap: 14 }}>
      {/* Header card */}
      <div className="card" style={{ padding: 16 }}>
        <div className="hstack" style={{ gap: 12 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 8,
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.IconWallet size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="hstack" style={{ gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{page.name}</h2>
              {page.system && (
                <span className="chip chip-info" style={{ fontSize: 10 }}>
                  系统预置
                </span>
              )}
              <span
                className={`chip ${page.status === '上架' ? 'chip-pos' : 'chip-warn'}`}
                style={{ fontSize: 10 }}
              >
                ● {page.status}
              </span>
              {dirty && (
                <span className="chip chip-warn" style={{ fontSize: 10 }}>
                  ● 有未保存的更改
                </span>
              )}
            </div>
            {/* 「支付场景」+「最近更新」两列 labeled 块（照设计稿：code pill + 复制按钮 + 1×30 分隔线） */}
            <div
              className="hstack"
              style={{ gap: 16, marginTop: 9, flexWrap: 'wrap', alignItems: 'center' }}
            >
              <div className="vstack" style={{ gap: 4 }}>
                <span className="label" style={{ fontSize: 9.5 }}>
                  支付场景 · scenario
                </span>
                <div className="hstack" style={{ gap: 4 }}>
                  <code
                    className="mono"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--accent)',
                      background: 'var(--accent-soft)',
                      padding: '2px 8px',
                      borderRadius: 5,
                      lineHeight: 1.5,
                    }}
                  >
                    {/* 接真映射：原型 demo 的 page.id 即场景串（charge_center 等）；真数据 id 是 UUID、
                        场景标识在 page.scenario——此块 label/复制语义都是「支付场景」，故取 scenario */}
                    {page.scenario}
                  </code>
                  <button
                    className="btn btn-ghost btn-icon"
                    style={{ width: 22, height: 22 }}
                    title="复制支付场景 ID"
                    onClick={() => {
                      navigator.clipboard?.writeText(page.scenario);
                      toast('已复制支付场景 ID', { tone: 'pos', icon: 'IconCopy' });
                    }}
                  >
                    <Icons.IconCopy size={11} />
                  </button>
                </div>
              </div>
              <div style={{ width: 1, height: 30, background: 'var(--border)' }} />
              <div className="vstack" style={{ gap: 4 }}>
                <span className="label" style={{ fontSize: 9.5 }}>
                  最近更新
                </span>
                <span
                  className="mono"
                  style={{ fontSize: 11.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}
                >
                  {fmtDateTime(page.updated)} · {page.by}
                </span>
              </div>
            </div>
          </div>
          <div className="hstack" style={{ gap: 6 }}>
            {/* 操作日志（接真）：打开配置变更历史抽屉（GET /audit-logs?resource=payment_pages&resource_id） */}
            <button
              className="btn btn-sm"
              title="查看该页面的配置变更历史"
              onClick={() => setLogsOpen(true)}
            >
              <Icons.IconHistory size={11} />
              操作日志
            </button>
            {/* 上架商品集合 + 展示顺序（ADM-PAY-S009）：与 placement 草稿互不影响 —— 该弹窗自成
                一次整组替换 PUT、保存即时生效，故不受本页「未保存改动」守卫与上下架态约束 */}
            {/* 写入口按 operator+ 收窄（后端 requireRole 为权威，此处只是别让 viewer 白配一遍
                再吃 403）——与同页「新建 / 保存」等写按钮同一套 RBAC UX */}
            <button
              className="btn btn-sm"
              onClick={() => setProductOrderOpen(true)}
              disabled={!canWrite}
              title={canWrite ? undefined : '权限不足（仅 root/admin/operator 可配置本页上架商品）'}
            >
              <Icons.IconList size={11} />
              上架商品
            </button>
            {/* 存量矛盾态收敛入口：页已下架却仍有已配 placement（C 端仍在供货）。下架态没有
                「页面下架」按钮可点，故单独给一个；后端 A8 §7.3 对 is_active:false 一律执行
                清空、不做幂等短路，正是为这条路径留的。operational_popup 不走级联故排除。 */}
            {page.status !== '上架' &&
              page.channels.length > 0 &&
              page.scenario !== 'operational_popup' && (
                <button
                  className="btn btn-sm btn-danger"
                  disabled={writeDisabled}
                  title={canWrite ? '本页已下架但仍有已配置渠道，客户端仍能取到商品' : '权限不足'}
                  onClick={() => setClearResidualOpen(true)}
                >
                  <Icons.IconAlert size={11} />
                  清空残留配置（{page.channels.length}）
                </button>
              )}
            {/* operational_popup 页不渲染上下架入口：其 is_active 无执行点（A8 §7.3 / ADM-PAY-S002），
                渲染出来就是假开关 —— 点了什么都不会发生。 */}
            {isPopupPage ? null : page.status === '上架' ? (
              <button
                className="btn btn-sm"
                disabled={writeDisabled}
                title={canWrite ? undefined : '权限不足'}
                onClick={() => setUnpublishOpen(true)}
              >
                <Icons.IconPause size={11} />
                页面下架
              </button>
            ) : (
              <button
                className="btn btn-sm btn-accent"
                disabled={configDisabled}
                title={
                  !canWrite
                    ? '权限不足'
                    : page.paywallsError
                      ? '渠道配置加载失败 · 刷新后再上架（避免覆盖残缺配置）'
                      : undefined
                }
                onClick={() => togglePageMutation.mutate(true)}
              >
                <Icons.IconCheck size={11} />
                页面上架
              </button>
            )}
            {/* 视觉条 ②：删除 button（系统预置页 disabled+lock；运营页 trash + 删除弹窗，ADM-PAY-S004）*/}
            {/* F1：删除仅 root/admin（canDelete），operator 不可删（级联软删 C 端 app_payment_methods 高危）*/}
            {page.system ? (
              <button
                className="btn btn-sm"
                disabled
                title="系统预置页面不可删除，仅可通过上下架控制可见性"
                style={{ opacity: 0.55, cursor: 'not-allowed' }}
              >
                <Icons.IconLock size={11} />
                删除
              </button>
            ) : (
              <button
                className="btn btn-sm"
                style={{ color: 'var(--neg)' }}
                disabled={!canDelete}
                title={
                  canDelete
                    ? '删除前校验该页 scenario 是否被 banner / push 引用'
                    : '权限不足（仅 root/admin 可删除支付页面）'
                }
                onClick={() => setDeleteOpen(true)}
              >
                <Icons.IconTrash size={11} />
                删除
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Channels and placements */}
      <div className="card" style={{ padding: '4px 18px 16px' }}>
        <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 500, fontSize: 13.5 }}>Paywall 配置 · 按支付渠道</div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>
            {isPopupPage ? (
              // operational_popup 的两条与普通页不同的规则（A8 §7.5）：各渠道共用一个 placement；
              // 弹窗用不用不看本页上下架，看运营弹窗场景上下架。不写出来运营会按普通页的心智操作。
              <>
                运营弹窗各渠道共用同一个 <span className="mono">Adapty placement_id</span> ·
                改已配置渠道会同步其它已配置渠道，首次配置新渠道须填与已配置渠道相同的值 ·
                弹窗是否出现由「运营管理 › 运营弹窗」场景上下架决定
              </>
            ) : (
              <>
                为每个渠道配置独立的 <span className="mono">Adapty placement_id</span> 与「启用 /
                停用」· 渠道启停与页面上下架为两层独立操作
              </>
            )}
          </div>
        </div>

        {/* 陈旧快照冲突的常驻标记：冲突后不自动刷新（保住草稿、也不换令牌），界面停在旧值，须明说并给出
            载入最新配置的入口。样式沿用本页 placement 状态卡的 warn 提示块。 */}
        {staleConflict && (
          <div
            className="card-flat"
            style={{
              marginTop: 14,
              padding: 10,
              background: 'var(--warn-soft)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              borderColor: 'transparent',
            }}
          >
            <Icons.IconAlert size={12} style={{ color: 'var(--warn)', flexShrink: 0 }} />
            <span style={{ fontSize: 11.5, color: 'var(--fg)' }}>
              渠道配置已被其他人改动，本次未保存 · 下方显示的可能不是最新值，请先载入最新配置再修改
            </span>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="btn btn-sm"
              disabled={reloadingLatest}
              onClick={() => {
                void reloadLatest();
              }}
            >
              <Icons.IconRefresh size={10} />
              {reloadingLatest ? '载入中…' : '载入最新配置'}
            </button>
          </div>
        )}

        {/* channel switcher + enable toggle（渠道启停与页面上下架解耦，照设计稿 PaywallPageDetail）*/}
        {/* 「未知」本身必须可见：策略组未就绪时下面的 chip 一概不挂、servable 也放宽，
            若不说明，运营看到的是一个「一切正常」的页面并据此做停用决策。 */}
        {!policiesKnown && (
          <div style={{ marginTop: 14, fontSize: 11, color: 'var(--fg-muted)' }}>
            {policies.isError
              ? '通道策略读取失败 · 下方渠道状态未经策略组校验，启停请以「App 详情 → 支付渠道 → 支付通道策略」为准'
              : paymentMethodOptions.isError
                ? '可选支付方式读取失败 · 成员归属的结算通道未知，下方渠道状态未经策略组校验'
                : '通道策略加载中 · 下方渠道状态尚未经策略组校验'}
          </div>
        )}
        <div
          className="hstack"
          style={{
            gap: 4,
            marginTop: 14,
            marginBottom: 14,
            alignItems: 'stretch',
            flexWrap: 'wrap',
          }}
        >
          {PAYWALL_CHANNEL_IDS.map((id) => ({
            id,
            name: chName(id),
            desc: paywallChannelDescOf(id),
          })).map((c) => {
            const active = channel === c.id;
            const on = enabled[c.id];
            return (
              // 卡片点击 = 选中该渠道以编辑 placement（active 时 accent 边/底）；
              // 右侧 Switch 用 stopPropagation 包裹，避免点开关连带切渠道。
              <div
                key={c.id}
                onClick={() => setChannel(c.id)}
                className="focusable"
                title="点击选中该渠道以编辑 placement"
                style={{
                  flex: '1 1 200px',
                  padding: 12,
                  borderRadius: 6,
                  border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
                  background: active ? 'var(--accent-soft)' : 'var(--bg-elev)',
                  cursor: 'pointer',
                  color: 'var(--fg)',
                }}
              >
                <div className="hstack" style={{ gap: 8 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 500 }}>{c.name}</span>
                  <span
                    className={`chip ${on ? 'chip-pos' : ''}`}
                    style={{ fontSize: 9.5, padding: '0 5px' }}
                  >
                    {on ? '已启用' : '已停用'}
                  </span>
                  <div style={{ flex: 1 }} />
                  <span
                    onClick={(e) => e.stopPropagation()}
                    title={on ? '停用该渠道' : '启用该渠道'}
                    style={{ display: 'inline-flex' }}
                  >
                    <Switch
                      key={`sw-${c.id}-${on}-${swNonce}`}
                      on={on}
                      disabled={placementWriteDisabled || channelToggleMutation.isPending}
                      onChange={(v) => requestToggle(c.id, v)}
                      size="sm"
                    />
                  </span>
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 4 }}>
                  {c.desc}
                </div>
                {/* 启停真值在通道策略组。四级表达，未就绪时一概不挂（未知 ≠ 已关闭）：
                    ① 全局组与任何覆盖组都不含 → 「不在通道策略内 · 客户端不展示」；
                    ② 不在全局组、仅某些覆盖组含 → 「仅 X 地区可用」；
                    ③ 在全局组、但某些覆盖组把它整套替换掉 → 「X 地区已排除」；
                    ④ 在全局组且无覆盖组排除 → 不挂。 */}
                {policiesKnown &&
                  (!servesAnywhere(c.id) ? (
                    // 全局组不含、且没有任何覆盖组含它 → 所有请求都拿不到
                    <div className="hstack" style={{ gap: 5, marginTop: 6 }}>
                      <span
                        className="chip"
                        style={{ fontSize: 9, padding: '0 5px' }}
                        title="该通道不在本 App 的全局通道策略、也不在任何商店地区覆盖组内，服务端不返回本渠道；启停请在「App 详情 → 支付渠道 → 支付通道策略」维护"
                      >
                        不在通道策略内 · 客户端不展示
                      </span>
                    </div>
                  ) : !inGlobalPolicy(c.id) ? (
                    // 仅靠覆盖组可用：其余地区（含 effective_store_region 为空时的回退）拿不到
                    <div className="hstack" style={{ gap: 5, marginTop: 6 }}>
                      <span
                        className="chip"
                        style={{ fontSize: 9, padding: '0 5px' }}
                        title={`该通道不在全局通道策略内，仅以下商店地区的覆盖组包含它：${regionsServing(c.id).join('、')}；其余地区回退全局策略、拿不到本渠道`}
                      >
                        仅 {regionsServing(c.id).join('、')} 地区可用
                      </span>
                    </div>
                  ) : regionsExcluding(c.id).length > 0 ? (
                    // 在全局组，但某些地区的覆盖组把它整套替换掉了（契约：覆盖不与全局求并集）
                    <div className="hstack" style={{ gap: 5, marginTop: 6 }}>
                      <span
                        className="chip"
                        style={{ fontSize: 9, padding: '0 5px' }}
                        title={`该通道在全局通道策略内，但以下商店地区的覆盖组未包含它（Region 覆盖是整套替换、不与全局求并集），这些地区拿不到本渠道：${regionsExcluding(c.id).join('、')}`}
                      >
                        {regionsExcluding(c.id).join('、')} 地区已排除
                      </span>
                    </div>
                  ) : null)}
                {/* 框架通道收单前置：未满足 → C 端不下发（与策略 chip 并列）；读取失败只标「无法判定」 */}
                {(() => {
                  const acquiring = acquiringStateOf(c.id);
                  const providerChannel = c.id.toUpperCase();
                  if (acquiring.problem === null && !acquiring.unknown) return null;
                  if (!isPaymentProviderChannel(providerChannel)) return null;
                  return (
                    <div className="hstack" style={{ gap: 5, marginTop: 6 }}>
                      <span
                        className="chip"
                        style={{ fontSize: 9, padding: '0 5px' }}
                        title={
                          acquiring.problem !== null
                            ? `本 App 的 ${c.name} 配置${acquiring.problem}，C 端不会下发该通道的支付方式；请联系 root / admin 在 ${c.name} 配置页处理`
                            : `读不到 ${c.name} 配置（读取失败或离线），无法判定 C 端是否下发该通道`
                        }
                      >
                        {acquiring.problem !== null
                          ? '收单前置未满足 · 客户端不展示'
                          : '收单前置无法判定'}
                      </span>
                      {/* 卡片点击 = 选中渠道；链接点击只跳配置页，不连带切渠道 */}
                      <Link
                        to={paymentProviderConfigRoute(providerChannel)}
                        style={{ fontSize: 10.5 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        去 {c.name} 配置
                      </Link>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 14, paddingTop: 4, alignItems: 'flex-start' }}>
          <div style={{ width: 220, flexShrink: 0, paddingTop: 6 }}>
            <div style={{ fontSize: 12.5, fontWeight: 500 }}>Adapty placement_id</div>
            <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.5 }}>
              {chName(channel)} 渠道的 placement · 客户端按 placement_id 拉取 Paywall · 需先在
              Adapty 后台完成同名 placement 配置
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div className="hstack" style={{ gap: 8, marginBottom: 8 }}>
              {isManual ? (
                // manual 兜底：自由手输（约定命名占位）；无 catalog 候选时也走这里
                <input
                  className="input mono"
                  value={placement}
                  disabled={placementWriteDisabled}
                  placeholder={`例如 ${expectedPl(channel)}`}
                  onChange={(e) => setPlacement(e.target.value)}
                  style={{ flex: 1, maxWidth: 360 }}
                />
              ) : (
                // list 模式：自研 Select 富选项（placement_name 主名 + mono placement_id + kind chip）。
                // Adapty placement 同时有 name（人类可读）与 id（developer_id，客户端按它拉 Paywall）→ 两者都展示；
                // name === id（Adapty 未配 name、server fallback 为 id）时不重复显示 id。
                <Select
                  value={placement}
                  disabled={placementWriteDisabled}
                  onChange={(e) => setPlacement(e.target.value)}
                  placeholder="选择 Adapty placement…"
                  style={{ flex: 1, maxWidth: 360 }}
                >
                  {placementOptions.map((p) => (
                    <option key={p.placement_id} value={p.placement_id}>
                      <span className="hstack" style={{ gap: 8, width: '100%' }}>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flexShrink: 1,
                            minWidth: 0,
                          }}
                        >
                          {p.placement_name}
                        </span>
                        {p.placement_name !== p.placement_id && (
                          <span
                            className="mono"
                            style={{ fontSize: 10.5, color: 'var(--fg-faint)', flexShrink: 0 }}
                          >
                            {p.placement_id}
                          </span>
                        )}
                        <span
                          className="chip"
                          style={{ fontSize: 9, padding: '0 5px', flexShrink: 0 }}
                        >
                          {placementKindLabel(p.placement_id)}
                        </span>
                        <span style={{ flex: 1 }} />
                      </span>
                    </option>
                  ))}
                </Select>
              )}
              <button
                className="btn btn-sm"
                disabled={plSyncing}
                title="从 Adapty 重新拉取 placement 列表"
                style={{ minWidth: 64, justifyContent: 'center' }}
                onClick={doSyncPl}
              >
                {plSyncing ? (
                  <>
                    <span className="pw-spinner" />
                    同步中
                  </>
                ) : (
                  <>
                    <Icons.IconRefresh size={11} />
                    同步
                  </>
                )}
              </button>
              <button
                className="btn btn-sm"
                title={`在 Adapty 控制台打开 ${placement || 'placements'}`}
                onClick={() => {
                  // 外链跳转 Adapty placements（纯前端 window.open，无需 SSO）；对齐设计稿 paywall.jsx
                  const url = `https://app.adapty.io/placements${
                    placement ? `?search=${encodeURIComponent(placement)}` : ''
                  }`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                  toast(`正在打开 Adapty · ${placement || 'placements'}`, {
                    tone: 'info',
                    icon: 'IconExternal',
                  });
                }}
              >
                <Icons.IconExternal size={11} />
                Adapty
              </button>
            </div>
            {/* 视觉条 ③：placement 状态卡片（校验基于真 Adapty 同步存在 plInList，非命名约定）：
                ① 同步中 / ② 手动输入（命中 catalog→绿，否则信息提示）/ ③ plInList 绿「已同步」/ ④ 未在同步列表/未选 warn。 */}
            {(() => {
              const chChip = (
                <span className="chip mono" style={{ fontSize: 10 }}>
                  {channel.toUpperCase()}
                </span>
              );
              // ① 同步中
              if (plSyncing) {
                return (
                  <div
                    className="card-flat"
                    style={{
                      padding: 10,
                      background: 'var(--bg-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span className="pw-spinner" />
                    <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                      正在从 Adapty 同步 placement 列表…
                    </span>
                    <div style={{ flex: 1 }} />
                    {chChip}
                  </div>
                );
              }
              // ② 手动输入态（无 catalog 候选 / 运营选手输）：手输值若真在 catalog 命中→绿；否则信息提示（无 catalog 可校验，信任运营）
              if (isManual) {
                return (
                  <div
                    className="card-flat"
                    style={{
                      padding: 10,
                      background: plInList ? 'var(--bg-subtle)' : 'var(--warn-soft)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      borderColor: plInList ? undefined : 'transparent',
                    }}
                  >
                    {plInList ? (
                      <>
                        <Icons.IconCheckCircle size={12} style={{ color: 'var(--pos)' }} />
                        <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                          <b style={{ color: 'var(--pos)', fontWeight: 600 }}>✓ Adapty 已同步</b> ·{' '}
                          <span className="mono">{placementProducts.length} 个产品</span> · 最近同步{' '}
                          {lastSync}
                        </span>
                      </>
                    ) : (
                      <>
                        <Icons.IconAlert
                          size={12}
                          style={{ color: 'var(--warn)', flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 11.5, color: 'var(--fg)' }}>
                          {adaptyCatalog.isError
                            ? '该 App 未配置 Adapty Key 或同步失败 · 可手输 placement_id，或去「设置 → 集成」配置 Adapty'
                            : '手动输入 · 请确认已在 Adapty 创建该 placement，否则客户端拉不到 Paywall'}
                        </span>
                      </>
                    )}
                    <div style={{ flex: 1 }} />
                    {placementOptions.length > 0 && (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setManualPl((m) => ({ ...m, [channel]: false }))}
                      >
                        改用列表选择
                      </button>
                    )}
                    {chChip}
                  </div>
                );
              }
              // ③ 所选 placement 真在 Adapty 同步列表里 → 绿色「已同步」（不看命名是否符约定）
              if (plInList) {
                return (
                  <div
                    className="card-flat"
                    style={{
                      padding: 10,
                      background: 'var(--bg-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Icons.IconCheckCircle size={12} style={{ color: 'var(--pos)' }} />
                    <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                      <b style={{ color: 'var(--pos)', fontWeight: 600 }}>✓ Adapty 已同步</b> ·{' '}
                      <span className="mono">{placementProducts.length} 个产品</span> · 最近同步{' '}
                      {lastSync}
                    </span>
                    <div style={{ flex: 1 }} />
                    {chChip}
                  </div>
                );
              }
              // ④ 选了 placement 但不在已同步列表（确知未配进 Adapty）/ 未选 → warn（启用·上架会被防呆拦）+ 手输兜底
              return (
                <div
                  className="card-flat"
                  style={{
                    padding: 10,
                    background: 'var(--warn-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    borderColor: 'transparent',
                  }}
                >
                  <Icons.IconAlert size={12} style={{ color: 'var(--warn)', flexShrink: 0 }} />
                  <span style={{ fontSize: 11.5, color: 'var(--fg)' }}>
                    {placement
                      ? '该 placement 不在已同步的 Adapty 列表 · 请确认已在 Adapty 创建该 placement（否则客户端拉不到 Paywall），或点「同步」重新拉取'
                      : '尚未选择 placement · 请从列表选择，或手动输入'}
                  </span>
                  <div style={{ flex: 1 }} />
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setManualPl((m) => ({ ...m, [channel]: true }))}
                  >
                    <Icons.IconEdit size={10} />
                    手动输入
                  </button>
                  {chChip}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Products preview */}
        <div style={{ marginTop: 20 }}>
          <div className="hstack" style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 500 }}>
              该 placement 下的产品 ({placementProducts.length})
            </div>
            <div className="mono muted" style={{ marginLeft: 6, fontSize: 11 }}>
              · 由 Adapty 承接 · 不可在此修改
            </div>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-sm"
              disabled={plSyncing}
              style={{ minWidth: 78, justifyContent: 'center' }}
              onClick={() => {
                // 同步 = 强制重拉 Adapty catalog（含 placement 产品富集，staleTime:0 旁路 30s 缓存）→ 完成弹该 placement 产品结果
                if (plSyncing) return;
                setPlSyncing(true);
                queryClient
                  .fetchQuery({
                    queryKey: ['adapty', 'catalog', app.id],
                    queryFn: () => adaptyApi.catalog(app.id, true),
                    staleTime: 0,
                  })
                  .then((data) => {
                    const n =
                      data?.placements?.find((pl) => pl.placement_id === placement)?.products
                        ?.length ?? 0;
                    toast(
                      n > 0
                        ? `已同步 · 该 placement 共 ${n} 个产品`
                        : '已同步 · 该 placement 的 paywall 暂无产品（请去 Adapty 后台为该 paywall 配置产品）',
                      { tone: n > 0 ? 'pos' : 'warn', icon: n > 0 ? 'IconRefresh' : 'IconAlert' },
                    );
                  })
                  .catch((err) =>
                    toast(
                      err instanceof Error
                        ? err.message
                        : '同步失败 · 请确认该 App 已配置 Adapty Key',
                      { tone: 'neg', icon: 'IconAlert' },
                    ),
                  )
                  .finally(() => setPlSyncing(false));
              }}
            >
              {plSyncing ? (
                <>
                  <span className="pw-spinner" />
                  同步中
                </>
              ) : (
                <>
                  <Icons.IconRefresh size={11} />
                  同步
                </>
              )}
            </button>
          </div>
          {placementProducts.length === 0 ? (
            <div
              className="card-flat"
              style={{
                padding: '14px 12px',
                background: 'var(--bg-subtle)',
                fontSize: 11.5,
                color: 'var(--fg-muted)',
              }}
            >
              {placement && plInList
                ? '该 placement 的 paywall 在 Adapty 下暂无产品 · 去 Adapty 后台为该 paywall 配置产品后点「同步」'
                : '该 placement 未在 Adapty 同步列表 · 先「同步」拉取，或确认已在 Adapty 创建该 placement 并配好 paywall'}
            </div>
          ) : (
            // 产品来自 Adapty paywall 的 products[]：内部 product_id + title + product_set（档位）；
            // 无 store 级 vendor_product_id / 价格 / coin·bonus（那些属业务效果，见「支付产品」tab）
            <Table className="tbl">
              <thead>
                <tr>
                  <th>产品</th>
                  <th>档位</th>
                  <th>
                    <span className="mono">product_id</span>（Adapty）
                  </th>
                </tr>
              </thead>
              <tbody>
                {placementProducts.map((p) => (
                  <tr key={p.product_id}>
                    <td style={{ fontWeight: 500 }}>{p.title}</td>
                    <td>
                      {p.product_set ? (
                        <span className="chip" style={{ fontSize: 10 }}>
                          {p.product_set}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--fg-faint)' }}>—</span>
                      )}
                    </td>
                    <td className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                      {p.product_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        {/* 视觉条 ④：保存配置（单按钮·随设计稿）—— 仅草稿存当前渠道 placement_id；页面上架/下架走顶栏按钮、
            渠道启停走开关，三者解耦。gate = configDisabled（RBAC/加载失败）|| !dirty（无未保存改动），对齐设计稿 !draftDirty。*/}
        <div
          className="hstack"
          style={{ marginTop: 16, paddingTop: 14, borderTop: '1px dashed var(--border)', gap: 8 }}
        >
          <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>所有改动将被记录到操作日志</span>
          {/* 草稿状态指示（照设计稿 draftDirty/draftSavedAt）：dirty 亮警示，保存成功后驻留「已保存」态 */}
          {dirty ? (
            <span
              className="hstack"
              style={{ gap: 4, fontSize: 11, color: 'var(--warn)', marginLeft: 10 }}
            >
              <Icons.IconEdit size={10} />
              有未保存的草稿改动
            </span>
          ) : (
            savedAtLabel && (
              <span
                className="hstack"
                style={{ gap: 4, fontSize: 11, color: 'var(--pos)', marginLeft: 10 }}
              >
                <Icons.IconCheckCircle size={10} />
                草稿已保存 · {savedAtLabel}
              </span>
            )
          )}
          <div style={{ flex: 1 }} />
          <div
            style={{
              textAlign: 'right',
              fontSize: 10.5,
              color: 'var(--fg-faint)',
              lineHeight: 1.45,
              marginRight: 4,
            }}
          >
            仅草稿保存当前渠道（{chName(channel)}）的 placement_id
            <br />
            不改变渠道启停与页面上下架状态
          </div>
          <button
            className="btn"
            title={
              !canWrite
                ? '权限不足'
                : page.paywallsError
                  ? '渠道配置加载失败 · 刷新后再保存（避免覆盖残缺配置）'
                  : !dirty
                    ? '没有未保存的改动'
                    : '草稿保存 placement_id · 不改变渠道启停与页面上下架'
            }
            // 渠道开关在途时也禁用：两笔写带同一个令牌，后到的一笔会被判成「被他人改过」
            disabled={placementWriteDisabled || !dirty || channelToggleMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            <Icons.IconCheck size={11} />
            保存配置
          </button>
        </div>
      </div>

      {chConfirm && (
        <ConfirmDialog
          title={chConfirm.title}
          message={chConfirm.message}
          details={chConfirm.details}
          tone={chConfirm.tone}
          confirmLabel={chConfirm.confirmLabel}
          secondaryLabel={chConfirm.secondaryLabel}
          onConfirm={chConfirm.onConfirm}
          onSecondary={chConfirm.onSecondary}
          onClose={() => setChConfirm(null)}
        />
      )}

      {unpublishOpen && (
        <ConfirmDialog
          title={`下架「${page.name}」？`}
          message={
            // operational_popup 页不走下架级联（页级 is_active 对它无执行点）→ 不提清空
            page.scenario === 'operational_popup'
              ? '下架后该页在后台标记为停用；本页的 Adapty placement 不会被清空，弹窗是否出现由「运营弹窗」场景上下架决定。'
              : page.channels.length > 0
                ? `下架将清空本页 ${page.channels.length} 个渠道的 Adapty placement 配置，客户端随即取不到该页商品；已购权益保留。placement 清空后不可恢复，重新上架需逐渠道重填。`
                : '下架后客户端不可见此付费页面，已购权益保留。本页当前无已配置渠道，下架不清空任何 placement。'
          }
          tone="warn"
          icon="IconPause"
          confirmLabel="页面下架"
          requireTyping={page.name}
          // 防御性：toggle 在途时不重复 mutate（ConfirmDialog 确认即关闭，trigger 已被 writeDisabled 禁用；
          // 此处再兜一层防极快双击穿透）。失败由 togglePageMutation.onError 弹错误 toast，不假成功。
          onConfirm={() => {
            if (!togglePageMutation.isPending) togglePageMutation.mutate(false);
          }}
          onClose={() => setUnpublishOpen(false)}
        />
      )}

      {clearResidualOpen && (
        <ConfirmDialog
          title={`清空「${page.name}」的残留渠道配置？`}
          message={`本页状态是下架，但仍有 ${page.channels.length} 个渠道配着 Adapty placement —— 客户端仍能取到该页商品。清空后这些 placement 置空、客户端立即取不到；清空不可恢复，重新供货需先上架再逐渠道重填。`}
          tone="warn"
          icon="IconAlert"
          confirmLabel="清空残留配置"
          requireTyping={page.name}
          onConfirm={() => {
            if (!togglePageMutation.isPending) togglePageMutation.mutate(false);
          }}
          onClose={() => setClearResidualOpen(false)}
        />
      )}

      {productOrderOpen && (
        <ProductOrderDialog
          pageId={page.id}
          pageName={page.name}
          appId={app.id}
          // 本页 placement 下的 Adapty product_id：取**各通道的并集**。契约要求同 scenario 各通道
          // 共用同一 placement（assertScenarioPlacementConsistent），正常情况下各通道相同；配置中途
          // 万一不一致，取并集比取当前 channel 更不容易把真属于本页的商品误折叠。
          //
          // **取 `page.placements`（已保存真值）而非 `placements`（本页草稿）**：排序弹窗保存即时
          // 生效，而草稿里的 placement 尚未生效；拿未生效的值当过滤依据，会在「改了 placement 没保存
          // 就去排序、随后取消草稿」时留下与实际 paywall 对不上的分区。
          isPlacementProductScopeVerified={
            Object.values(page.placements).filter(Boolean).length > 0 &&
            Object.values(page.placements)
              .filter(Boolean)
              .every(
                (id) =>
                  adaptyCatalog.data?.placements.find((placement) => placement.placement_id === id)
                    ?.is_product_scope_verified === true,
              )
          }
          placementProductIds={[
            ...new Set(
              PAYWALL_CHANNEL_IDS.flatMap(
                (ch) =>
                  placementOptions
                    .find((p) => p.placement_id === (page.placements[ch] ?? ''))
                    ?.products.map((pr) => pr.product_id) ?? [],
              ),
            ),
          ]}
          onClose={() => setProductOrderOpen(false)}
        />
      )}

      {deleteOpen && <DeletePaywallDialog page={page} onClose={() => setDeleteOpen(false)} />}
      {logsOpen && (
        <PaywallAuditDrawer
          pageId={page.id}
          pageName={page.name}
          onClose={() => setLogsOpen(false)}
        />
      )}
    </div>
  );
}
